import React, { useState, useRef, useEffect } from 'react'
import { OmButton, OmInput, OmSelect, OmSpinner, OmCheckbox, omMessage } from '@owma/kit'

import mailboxApiHelper from 'services/mailbox-api'
import openSelectFolder from 'popups/SelectFolder'
import i18n from 'i18n'
import classNames from 'classnames'

import { confirmBase } from 'utils/ui'
import SharedEmailSelect from 'pages/main/components/EmailForm/SharedEmailSelect'
import chevronDownIcon from 'assets/images/chevron-down-24.svg'

const selectSuffixIcon = <img src={chevronDownIcon} alt="" width={24} height={24} />

interface EmailInputProps {
  className?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  userEmail?: string
}

const EmailInput: React.FC<EmailInputProps> = ({
  className,
  value,
  onChange,
  placeholder,
  userEmail,
}) => {
  const ref = useRef<any>(null)
  const initialized = useRef(false)

  useEffect(() => {
    if (ref.current && value && !initialized.current) {
      initialized.current = true
      ref.current.setEmailList([value])
    }
  }, [ref.current])

  let userDomain = null
  if (userEmail) {
    const parts = userEmail.split('@')
    if (parts.length > 1) {
      userDomain = parts[1]
    }
  }

  return (
    <div className={className}>
      <SharedEmailSelect
        innerRef={ref}
        placeholder={placeholder}
        onChange={(refObj: any) => {
          const emails = refObj?.getEmailList?.() || []
          onChange(emails.join('; '))
        }}
        innerDomain={userDomain}
        maxCount={1}
      />
    </div>
  )
}

import type { FilterCondition, FilterAction, FilterDto } from './index'
import styles from './style.module.scss'

interface EditorCondition {
  _key: string
  test: string
  value: string
  option: string
}

interface EditorAction {
  _key: string
  action: string
  arg: string
  folderName: string
}

let _keySeq = 0
const genKey = (prefix: string): string => `${prefix}_${Date.now()}_${++_keySeq}`

const EMAIL_TESTS = ['from', 'to', 'cc']
const INCLUDE_TESTS = ['subject', 'body', 'subjectOrBody']
const ALL_CONDITION_TESTS = [...EMAIL_TESTS, ...INCLUDE_TESTS]
const ALL_ACTION_TYPES = ['move', 'copy', 'forward', 'redirect', 'markRead', 'delete', 'markJunk']
const FOLDER_ACTIONS = ['move', 'copy']
const EMAIL_ACTIONS = ['forward', 'redirect']
const NO_ARG_ACTIONS = ['delete', 'markRead', 'markJunk']

const editorCondKeyMap: Record<string, string> = {
  from: 'serverRules.editor.cond.from',
  to: 'serverRules.editor.cond.to',
  cc: 'serverRules.editor.cond.cc',
  subject: 'serverRules.editor.cond.subject',
  body: 'serverRules.editor.cond.body',
  subjectOrBody: 'serverRules.editor.cond.subjectOrBody',
}

const actionKeyMap: Record<string, string> = {
  move: 'serverRules.action.move',
  copy: 'serverRules.action.copy',
  delete: 'serverRules.action.delete',
  markRead: 'serverRules.action.markRead',
  forward: 'serverRules.action.forward',
  redirect: 'serverRules.action.redirect',
  markJunk: 'serverRules.action.markJunk',
}

function makeDefaultCondition(): EditorCondition {
  return { _key: genKey('c'), test: '', value: '', option: 'all' }
}

function makeDefaultAction(): EditorAction {
  return { _key: genKey('a'), action: '', arg: '', folderName: '' }
}

function conditionFromDto(cond: FilterCondition): EditorCondition {
  return {
    _key: genKey('c'),
    test: cond.test,
    value: cond.value || '',
    option: cond.option || 'all',
  }
}

function actionFromDto(act: FilterAction): EditorAction {
  return { _key: genKey('a'), action: act.action, arg: act.arg || '', folderName: act.arg || '' }
}

function conditionToDto(cond: EditorCondition): FilterCondition {
  const dto: any = {
    test: cond.test,
    value: cond.value,
  }
  if (EMAIL_TESTS.includes(cond.test) && cond.option) {
    dto.option = cond.option
  }
  return dto
}

function actionToDto(act: EditorAction): FilterAction {
  const dto: FilterAction = { action: act.action as FilterAction['action'] }
  if (!NO_ARG_ACTIONS.includes(act.action)) {
    dto.arg = act.arg
  }
  return dto
}

interface ServerRuleEditorProps {
  mode: 'add' | 'edit'
  filter?: FilterDto
  onSave: () => void
  userEmail?: string
  saveRef: { current: ((closeFunc: () => void) => void) | null }
}

const ServerRuleEditor: React.FC<ServerRuleEditorProps> = ({
  mode,
  filter,
  onSave,
  saveRef,
  userEmail,
}) => {
  const [name, setName] = useState<string>(filter?.name || '')
  const [conditions, setConditions] = useState<EditorCondition[]>(() => {
    if (filter?.rule?.conditions?.length) {
      const filtered = filter.rule.conditions.filter((c) => c.test !== 'always')
      if (filtered.length > 0) {
        return filtered.map((c) => conditionFromDto(c))
      }
    }
    return [makeDefaultCondition()]
  })
  const [actions, setActions] = useState<EditorAction[]>(() => {
    if (filter?.rule?.actions?.length) {
      return filter.rule.actions.map((a) => actionFromDto(a))
    }
    return [makeDefaultAction()]
  })
  const [stop, setStop] = useState<boolean>(
    filter?.rule?.stop !== undefined ? filter.rule.stop : true,
  )
  const [saving, setSaving] = useState<boolean>(false)

  const updateCondition = (key: string, updates: Partial<EditorCondition>): void => {
    setConditions((prev) => prev.map((c) => (c._key === key ? { ...c, ...updates } : c)))
  }

  const removeCondition = (key: string): void => {
    setConditions((prev) => {
      const filtered = prev.filter((c) => c._key !== key)
      return filtered.length > 0 ? filtered : [makeDefaultCondition()]
    })
  }

  const addCondition = (): void => {
    setConditions((prev) => [...prev, makeDefaultCondition()])
  }

  const updateAction = (key: string, updates: Partial<EditorAction>): void => {
    setActions((prev) => prev.map((a) => (a._key === key ? { ...a, ...updates } : a)))
  }

  const removeAction = (key: string): void => {
    setActions((prev) => prev.filter((a) => a._key !== key))
  }

  const addAction = (): void => {
    setActions((prev) => [...prev, makeDefaultAction()])
  }

  const getMailboxFullPath = (mailbox: any): string => {
    const parts: string[] = []
    let cur = mailbox
    while (cur) {
      const name = cur.data?.name || ''
      if (name) parts.unshift(name)
      cur = cur.getParentMailbox?.()
    }
    return parts.join('/')
  }

  const handleSelectFolder = (actionKey: string): void => {
    openSelectFolder({
      title: i18n.t('serverRules.editor.selectFolder'),
      okText: i18n.t('common.button.ok'),
      tabs: ['folders'],
      hideTabs: true,
      onSelect: (id: any, mailbox: any) => {
        const displayName = mailbox?.getDisplayName?.() || mailbox?.data?.name || id
        const fullPath = getMailboxFullPath(mailbox) || id
        console.log('server rule action - action path - ', fullPath)
        updateAction(actionKey, { arg: fullPath, folderName: displayName })
      },
    })
  }

  const isValidEmail = (value: string): boolean => {
    if (!value) return false
    return value.split(';').every((v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()))
  }

  const handleSave = (closeFunc: () => void): void => {
    const activeConditions = conditions.filter((c) => c.test !== '')
    for (const cond of activeConditions) {
      if (
        EMAIL_TESTS.includes(cond.test) &&
        cond.option !== 'domain' &&
        !isValidEmail(cond.value)
      ) {
        omMessage.warning(i18n.t('serverRules.editor.invalidEmail'))
        return
      }
    }

    const activeActions = actions.filter((a) => a.action !== '')
    for (const act of activeActions) {
      if (EMAIL_ACTIONS.includes(act.action) && !isValidEmail(act.arg)) {
        omMessage.warning(i18n.t('serverRules.editor.invalidEmail'))
        return
      }
    }

    const filterConditions = activeConditions.map(conditionToDto)
    const filterActions = activeActions.map(actionToDto)

    if (filterConditions.length === 0) {
      filterConditions.push({ test: 'always', value: '' })
    }

    const ruleData: any = {
      name: name || undefined,
      rule: {
        conditions: filterConditions,
        actions: filterActions,
        stop: stop || undefined,
      },
    }

    setSaving(true)
    const apiCall =
      mode === 'edit' && filter
        ? mailboxApiHelper.filter.put(filter.id, ruleData)
        : mailboxApiHelper.filter.post(ruleData)

    apiCall
      .then(() => {
        onSave()
        closeFunc()
      })
      .catch((err: any) => {
        setSaving(false)
        omMessage.error(err?.message || 'Failed to save')
      })
  }

  saveRef.current = handleSave

  const usedTests = conditions.map((c) => c.test).filter((t) => t !== '')

  const getTestOptions = (currentTest: string) =>
    ALL_CONDITION_TESTS.filter((t) => t === currentTest || !usedTests.includes(t)).map((t) => ({
      value: t,
      /* i18next-extract-disable-next-line */
      label: i18n.t(editorCondKeyMap[t] || t),
    }))

  const optionOptions = [
    { value: 'all', label: i18n.t('serverRules.editor.option.email') },
    { value: 'domain', label: i18n.t('serverRules.editor.option.domain') },
  ]

  const usedActions = actions.map((a) => a.action).filter((a) => a !== '')

  const getActionOptions = (currentAction: string) =>
    ALL_ACTION_TYPES.filter((a) => a === currentAction || !usedActions.includes(a)).map((a) => ({
      value: a,
      /* i18next-extract-disable-next-line */
      label: i18n.t(actionKeyMap[a] || a),
    }))

  const allConditionsFilled = conditions.every((c) => c.test !== '')
  const allConditionsUsed = conditions.length >= 6
  const allActionsFilled = actions.every((a) => a.action !== '')
  const allActionsUsed = actions.length >= 7

  return (
    <OmSpinner spinning={saving}>
      <div className={styles.editorForm}>
        <div className={styles.nameInput}>
          <OmInput
            value={name}
            onChange={(value) => setName(value)}
            placeholder={i18n.t('serverRules.editor.ruleNamePlaceholder')}
          />
        </div>

        <div className={styles.formGroup}>
          <div className={styles.formLabel}>{i18n.t('serverRules.editor.conditions')}</div>
          {conditions.map((cond, idx) => (
            <div key={cond._key} className={styles.conditionRow}>
              <OmSelect
                className={styles.fieldSelect}
                suffixIcon={selectSuffixIcon}
                value={cond.test || undefined}
                placeholder={
                  idx === 0
                    ? i18n.t('serverRules.editor.selectCondition')
                    : i18n.t('serverRules.editor.selectAnd')
                }
                onChange={(val) =>
                  updateCondition(cond._key, { _key: genKey('c'), test: val as string, value: '' })
                }
                options={getTestOptions(cond.test)}
              />
              {cond.test !== '' && EMAIL_TESTS.includes(cond.test) && cond.option !== 'domain' && (
                <EmailInput
                  className={styles.valueInput}
                  value={cond.value}
                  onChange={(val) => updateCondition(cond._key, { value: val })}
                  placeholder={i18n.t('serverRules.editor.emailPlaceholder')}
                  userEmail={userEmail}
                />
              )}
              {cond.test !== '' && EMAIL_TESTS.includes(cond.test) && cond.option === 'domain' && (
                <OmInput
                  className={styles.textValueInput}
                  value={cond.value}
                  onChange={(value) => updateCondition(cond._key, { value })}
                  placeholder={i18n.t('serverRules.editor.domainPlaceholder')}
                />
              )}
              {cond.test !== '' && INCLUDE_TESTS.includes(cond.test) && (
                <OmInput
                  className={styles.textValueInput}
                  value={cond.value}
                  onChange={(value) => updateCondition(cond._key, { value })}
                  placeholder={i18n.t('serverRules.editor.valuePlaceholder')}
                />
              )}
              {cond.test !== '' && EMAIL_TESTS.includes(cond.test) && (
                <OmSelect
                  className={styles.optionSelect}
                  suffixIcon={selectSuffixIcon}
                  value={cond.option}
                  onChange={(val) =>
                    updateCondition(cond._key, { option: val as string, value: '' })
                  }
                  options={optionOptions}
                />
              )}
              {cond.test !== '' && (
                <OmButton
                  className={styles.removeBtn}
                  variant="text"
                  onClick={() => removeCondition(cond._key)}
                  icon={<span>✕</span>}
                />
              )}
            </div>
          ))}
          {allConditionsFilled && !allConditionsUsed && (
            <OmButton className={styles.addBtn} onClick={addCondition}>
              {i18n.t('serverRules.editor.addCondition')}
            </OmButton>
          )}
        </div>

        <div className={styles.formGroup}>
          <div className={styles.formLabel}>{i18n.t('serverRules.editor.actions')}</div>
          {actions.map((act, idx) => (
            <div key={act._key} className={styles.actionRow}>
              <OmSelect
                className={styles.fieldSelect}
                suffixIcon={selectSuffixIcon}
                value={act.action || undefined}
                placeholder={
                  idx === 0
                    ? i18n.t('serverRules.editor.selectAction')
                    : i18n.t('serverRules.editor.selectAnd')
                }
                onChange={(val) => {
                  const updates: Partial<EditorAction> = { action: val as string }
                  if (
                    NO_ARG_ACTIONS.includes(val as string) ||
                    FOLDER_ACTIONS.includes(val as string) !== FOLDER_ACTIONS.includes(act.action)
                  ) {
                    updates.arg = ''
                    updates.folderName = ''
                  }
                  updateAction(act._key, updates)
                }}
                options={getActionOptions(act.action)}
              />
              {act.action !== '' && FOLDER_ACTIONS.includes(act.action) && (
                <OmSelect
                  className={styles.folderSelect}
                  suffixIcon={selectSuffixIcon}
                  value={act.arg || undefined}
                  placeholder={i18n.t('serverRules.editor.selectFolder')}
                  onChange={() => handleSelectFolder(act._key)}
                  options={
                    act.arg
                      ? [{ value: act.arg, label: (act.folderName || act.arg).split('/').pop() }]
                      : []
                  }
                />
              )}
              {act.action !== '' && EMAIL_ACTIONS.includes(act.action) && (
                <EmailInput
                  className={styles.actionValueInput}
                  value={act.arg}
                  onChange={(val) => updateAction(act._key, { arg: val })}
                  placeholder={i18n.t('serverRules.editor.emailPlaceholder')}
                />
              )}
              {act.action !== '' && (
                <OmButton
                  className={styles.removeBtn}
                  variant="text"
                  onClick={() => removeAction(act._key)}
                  icon={<span>✕</span>}
                />
              )}
            </div>
          ))}
          {allActionsFilled && !allActionsUsed && (
            <OmButton className={styles.addBtn} onClick={addAction}>
              {i18n.t('serverRules.editor.addAction')}
            </OmButton>
          )}
        </div>

        <div className={styles.stopCheck}>
          <OmCheckbox checked={stop} onChange={(checked) => setStop(checked)}>
            {i18n.t('serverRules.editor.stopProcessing')}
          </OmCheckbox>
        </div>
      </div>
    </OmSpinner>
  )
}

interface EditServerRuleOptions {
  mode: 'add' | 'edit'
  filter?: FilterDto
  userEmail?: string
  onSave: () => void
}

export function openEditServerRule(options: EditServerRuleOptions): void {
  const { mode, filter, userEmail, onSave } = options
  /* i18next-extract-disable-next-line */
  const title = i18n.t(
    mode === 'edit' ? 'serverRules.editor.titleEdit' : 'serverRules.editor.titleAdd',
  )

  const saveRef: { current: ((closeFunc: () => void) => void) | null } = { current: null }

  confirmBase({
    wrapClassName: classNames(styles.serverRuleEditor),
    title,
    content: (
      <ServerRuleEditor
        mode={mode}
        filter={filter}
        onSave={onSave}
        saveRef={saveRef}
        userEmail={userEmail}
      />
    ),
    okText: i18n.t('common.button.save'),
    cancelText: i18n.t('common.button.cancel'),
    width: 638,
    maskClosable: false,
    okButtonProps: {
      className: classNames('type1', 'h38'),
    },
    cancelButtonProps: {
      className: classNames('h38'),
    },
    onOk: (closeFunc: () => void): void => {
      if (saveRef.current) {
        saveRef.current(closeFunc)
      }
    },
  })
}

export default ServerRuleEditor
